import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Clipboard,
  Compass,
  FileImage,
  Leaf,
  Lock,
  MessagesSquare,
  Sparkles,
  Target,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

import {
  bankDetails,
  clearSubmissions,
  loadSubmissions,
  saveSubmission,
} from "@/mock";

// ✅ Supabase client
import { supabase, SUPABASE_BUCKET } from "@/lib/supabaseClient";

const CTA_CLASS =
  "relative h-11 rounded-full bg-white px-6 text-base font-semibold text-black shadow-[0_0_0_1px_rgba(255,255,255,0.35),0_16px_45px_rgba(255,255,255,0.10),0_0_30px_rgba(255,255,255,0.18)] transition-colors hover:bg-white/95";
const CTA_CLASS_SM =
  "relative h-10 rounded-full bg-white px-5 text-[0.95rem] font-semibold text-black shadow-[0_0_0_1px_rgba(255,255,255,0.35),0_14px_40px_rgba(255,255,255,0.10),0_0_26px_rgba(255,255,255,0.18)] transition-colors hover:bg-white/95";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_farmer-system/artifacts/8xnv0rwx_WhatsApp%20Image%202025-12-16%20at%2016.57.01.jpeg";

const cx = (...classes) => classes.filter(Boolean).join(" ");

// ✅ Reveal robusto: NUNCA deja la página en blanco.
// Solo oculta/animación cuando JS está listo (html[data-reveal-ready="true"])
const useRevealOnScroll = () => {
  useEffect(() => {
    document.documentElement.setAttribute("data-reveal-ready", "true");

    const nodes = Array.from(document.querySelectorAll("[data-reveal='true']"));
    if (!nodes.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) e.target.setAttribute("data-revealed", "true");
        }
      },
      { rootMargin: "-10% 0px -20% 0px", threshold: 0.12 }
    );

    nodes.forEach((n) => io.observe(n));
    return () => {
      io.disconnect();
      document.documentElement.removeAttribute("data-reveal-ready");
    };
  }, []);
};

const Pill = ({ children, icon: Icon }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium tracking-wide text-white/80 backdrop-blur-xl">
    {Icon ? <Icon className="h-3.5 w-3.5 text-white/70" /> : null}
    {children}
  </span>
);

const SectionTitle = ({ kicker, title, subtitle }) => (
  <div className="max-w-3xl">
    {kicker ? (
      <div className="mb-3">
        <Pill icon={Sparkles}>{kicker}</Pill>
      </div>
    ) : null}
    <h2 className="text-pretty text-2xl font-semibold tracking-tight text-white sm:text-3xl">
      {title}
    </h2>
    {subtitle ? (
      <p className="mt-3 text-pretty text-sm leading-relaxed text-white/70 sm:text-base">
        {subtitle}
      </p>
    ) : null}
  </div>
);

const FeatureCard = ({ icon: Icon, title, body }) => (
  <Card className="group relative overflow-hidden border-white/10 bg-white/5 backdrop-blur-2xl">
    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <div className="absolute -left-10 -top-10 h-48 w-48 rounded-full bg-white/8 blur-2xl" />
      <div className="absolute -bottom-12 -right-12 h-56 w-56 rounded-full bg-white/6 blur-2xl" />
    </div>
    <CardHeader className="relative">
      <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-black/30">
        <Icon className="h-5 w-5 text-white/85" />
      </div>
      <CardTitle className="text-base text-white">{title}</CardTitle>
      <CardDescription className="text-white/60">{body}</CardDescription>
    </CardHeader>
  </Card>
);

const BankDetailsBlock = ({ onCopy }) => {
  const lines = useMemo(() => {
    const b = bankDetails;
    return [
      `Banco: ${b.bank}`,
      `Tipo: ${b.accountType}`,
      `Número de cuenta: ${b.accountNumber}`,
      `Titular: ${b.holder}`,
      `Cédula: ${b.id}`,
    ];
  }, []);

  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-5 backdrop-blur-2xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-medium tracking-wide text-white/60">
            Datos bancarios
          </p>
          <ul className="mt-3 space-y-1 text-sm text-white/85">
            {lines.map((l) => (
              <li key={l} className="font-medium">
                {l}
              </li>
            ))}
          </ul>
        </div>
        <Button
          type="button"
          onClick={onCopy}
          variant="outline"
          className="h-10 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
        >
          <Clipboard className="h-4 w-4" />
          Copiar
        </Button>
      </div>
      <Separator className="my-4 bg-white/10" />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-white">
            Obtén tu Montaje Web por {""}
            <span className="text-white">${bankDetails.reservationAmount}</span>
          </p>
          <p className="mt-1 text-xs leading-relaxed text-white/60">
            Total inversión (${bankDetails.fullInvestment}).
          </p>
        </div>
        <Badge className="w-fit rounded-full bg-white/10 text-white hover:bg-white/10">
          Transferencia bancaria
        </Badge>
      </div>
    </div>
  );
};

const SubmissionRow = ({ s }) => (
  <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-2xl sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold text-white">{s.fullName}</p>
      <p className="truncate text-xs text-white/60">{s.email}</p>
      <p className="mt-2 text-xs text-white/55">
        Enviado: {new Date(s.createdAt).toLocaleString()}
      </p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <Badge className="rounded-full bg-white/10 text-white hover:bg-white/10">
        Pendiente de verificación
      </Badge>
      <Badge
        variant="outline"
        className="rounded-full border-white/15 bg-black/30 text-white/80"
      >
        <FileImage className="mr-1 h-3.5 w-3.5" />
        {s.proofName || "Sin archivo"}
      </Badge>
    </div>
  </div>
);

export default function LandingPage() {
  useRevealOnScroll();

  const pagoRef = useRef(null);
  const [submissions, setSubmissions] = useState(() => loadSubmissions());
  const [busy, setBusy] = useState(false);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);

  const scrollToPago = () => {
    const el = pagoRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onCopyBank = async () => {
    const b = bankDetails;
    const text = [
      `Banco Produbanco`,
      `Cuenta de Ahorros`,
      `N° ${b.accountNumber}`,
      `Titular: ${b.holder}`,
      `Cédula: ${b.id}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Datos bancarios copiados.");
    } catch {
      toast.error("No se pudo copiar. Copia manualmente los datos.");
    }
  };

  const validate = () => {
    if (!fullName.trim()) return "Escribe tu nombre completo.";
    if (!email.trim()) return "Escribe tu email.";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return "Email no válido.";
    if (!file) return "Sube la captura (PNG o JPG).";
    if (file && !["image/png", "image/jpeg"].includes(file.type))
      return "Formato inválido. Solo PNG o JPG.";
    if (file && file.size > 3 * 1024 * 1024)
      return "Archivo muy pesado. Máximo 3MB (en esta demo).";
    return null;
  };

  // ✅ Supabase (Storage + DB) y mantiene localStorage para "Tus envíos"
  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setBusy(true);
    try {
      // 1) Subir imagen a Supabase Storage
      const ext = file.name.split(".").pop().toLowerCase();
      const filePath = `receipts/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(SUPABASE_BUCKET)
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        console.error(uploadError);
        toast.error("Error subiendo la captura.");
        return;
      }

      // 2) Guardar registro en la tabla
      const { error: insertError } = await supabase.from("landing_leads").insert({
        name: fullName.trim(),
        email: email.trim(),
        screenshot_path: filePath,
      });

      if (insertError) {
        console.error(insertError);
        toast.error("Error guardando tus datos.");
        return;
      }

      // 3) Mantener UI local (NO romper nada existente)
      const payload = {
        id: crypto.randomUUID(),
        fullName: fullName.trim(),
        email: email.trim(),
        proofName: file.name,
        createdAt: new Date().toISOString(),
      };

      const next = saveSubmission(payload);
      setSubmissions(next);

      setFullName("");
      setEmail("");
      setFile(null);

      toast.success("Comprobante enviado correctamente.");
    } catch (err) {
      console.error(err);
      toast.error("Error inesperado al enviar el comprobante.");
    } finally {
      setBusy(false);
    }
  };

  const onResetDemo = () => {
    clearSubmissions();
    setSubmissions([]);
    toast("Demo reiniciada.");
  };

  return (
    <div className="min-h-screen bg-[#0b0c0d] text-white">
      {/* Decorative background */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.08),transparent_45%),radial-gradient(circle_at_80%_30%,rgba(217,251,6,0.10),transparent_40%),radial-gradient(circle_at_50%_90%,rgba(255,255,255,0.06),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:72px_72px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/10 to-black/40" />
      </div>

      {/* Header */}
<header className="sticky top-0 z-40 border-b border-white/10 bg-black/40 backdrop-blur-2xl">
  <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5">
        <img
          src={LOGO_URL}
          alt="Logo Glich"
          className="h-5 w-5 object-contain"
        />
      </div>
      <div className="leading-tight">
        <p className="text-sm font-semibold text-white">GLICH BUSINESS</p>
      </div>
    </div>
  </div>
</header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-14 sm:px-8 sm:pb-20 sm:pt-20">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7" data-reveal="true">
            <div className="flex flex-wrap gap-2">
              <Pill icon={Target}>
                Diseñado para uso inmediato de 40 cuidadores
              </Pill>
              <Pill icon={BadgeCheck}>
                Exclusiva configuración técnica y carga de contenido
              </Pill>
            </div>

            <h1 className="mt-7 text-pretty text-4xl font-semibold tracking-tight text-white sm:text-5xl">
              <span className="font-extrabold">MONTAJE BÁSICO WEB</span>
              <span className="block mt-3 text-white/75 text-2xl sm:text-3xl">
                Implementación técnica del curso "Apego Seguro", listo para
                que los cuidadores accedan y completen la capacitación.
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-pretty text-sm leading-relaxed text-white/70 sm:text-base">
              Sitio web funcional. Con posibilad de actualizaciones futuras sin inversión principal.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button onClick={scrollToPago} className={CTA_CLASS}>
                Comenzar Ahora
                <ArrowRight className="h-4 w-4" />
              </Button>
              <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/60 backdrop-blur-2xl">
                Continúa con la adquisición de tu web con ${bankDetails.reservationAmount} y envía tu comprobante.
              </div>
            </div>
          </div>

          <div className="lg:col-span-5" data-reveal="true">
            <Card className="relative overflow-hidden border-white/10 bg-white/5 backdrop-blur-2xl">
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-16 right-[-40px] h-56 w-56 rounded-full bg-white/10 blur-3xl" />
                <div className="absolute -bottom-16 left-[-60px] h-64 w-64 rounded-full bg-white/8 blur-3xl" />
              </div>
              <CardHeader className="relative">
                <CardTitle className="text-base text-white">Qué vas a lograr</CardTitle>
                <CardDescription className="text-white/60">
                </CardDescription>
              </CardHeader>
              <CardContent className="relative grid gap-3">
                {[
                  {
                    icon: Compass,
                    title: "Publicación del curso en web",
                    body: "Navegación simple módulo-por-módulo",
                  },
                  {
                    icon: MessagesSquare,
                    title: "Área privada para 40 cuidadores",
                    body: "Carga del contenido en texto por módulos",
                  },
                  {
                    icon: Lock,
                    title: "Resultado",
                    body: "Sitio funcional y listo para ejecutar ",
                  },
                ].map((i) => (
                  <div
                    key={i.title}
                    className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/30 p-3"
                  >
                    <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                      <i.icon className="h-4 w-4 text-white/85" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{i.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-white/60">
                        {i.body}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
              <CardFooter className="relative">
                <Button
                  onClick={scrollToPago}
                  variant="outline"
                  className="h-10 w-full rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                >
                  Comenzar Ahora
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* Payment section */}
      <section
        ref={pagoRef}
        id="pago"
        className="mx-auto max-w-6xl scroll-mt-28 px-5 py-16 sm:px-8"
        data-reveal="true"
      >
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionTitle
              kicker="Comenzar"
              title="Haz la transferencia y sube el comprobante"
              subtitle="Este paso confirma tu compra"
            />
            <div className="mt-6">
              <BankDetailsBlock onCopy={onCopyBank} />
            </div>
            <div className="mt-4 text-xs text-white/55">
              
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="border-white/10 bg-white/5 backdrop-blur-2xl">
              <CardHeader>
                <CardTitle className="text-white">Enviar comprobante</CardTitle>
                <CardDescription className="text-white/60">
                  Nombre completo + email + captura (PNG/JPG).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={onSubmit} className="grid gap-4">
                  <div className="grid gap-2">
                    <Label className="text-white/80">Nombre completo</Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Tu nombre y apellido"
                      className="h-11 border-white/15 bg-black/30 text-white placeholder:text-white/40 focus-visible:ring-white/35"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-white/80">Email</Label>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      type="email"
                      className="h-11 border-white/15 bg-black/30 text-white placeholder:text-white/40 focus-visible:ring-white/35"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-white/80">Captura del comprobante</Label>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="h-11 border-white/15 bg-black/30 text-white file:text-white/80 focus-visible:ring-white/35"
                    />
                    <p className="text-xs text-white/55">
                      Tip: si tu captura pesa mucho, envíala en calidad “media” desde tu teléfono.
                    </p>
                  </div>

                  <Button
                    disabled={busy}
                    type="submit"
                    className={cx(CTA_CLASS, "mt-2", busy ? "opacity-70" : "")}
                  >
                    {busy ? "Enviando…" : "Comenzar Ahora"}
                    {!busy ? <ArrowRight className="h-4 w-4" /> : null}
                  </Button>
                </form>

                {submissions.length ? (
                  <div className="mt-8">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-white">Tus envíos</p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onResetDemo}
                        className="h-9 rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                      >
                        Reiniciar
                      </Button>
                    </div>
                    <div className="grid gap-3">
                      {submissions.map((s) => (
                        <SubmissionRow key={s.id} s={s} />
                      ))}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
{/* Footer (bloque anterior eliminado, se conserva SOLO este) */}
<footer className="border-t border-white/10 bg-black/40 backdrop-blur-2xl">
  <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
    <div className="flex flex-col gap-4 sm:items-end">
      <div className="flex items-center gap-3">
        <div className="rounded-2xl border border-white/15 bg-white/8 p-2 backdrop-blur-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.22),0_18px_55px_rgba(0,0,0,0.55),0_0_24px_rgba(255,255,255,0.10)]">
          <img
            src={LOGO_URL}
            alt="Logo"
            className="h-12 w-12 rounded-xl object-cover"
          />
        </div>
        <div className="leading-tight">
          <p className="text-xs font-medium tracking-wide text-white/70">
            GLICH BUSINESS
          </p>
          <p className="text-[11px] text-white/45">
            © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  </div>
</footer>


      {/* Animation helpers */}
      <style>{`
        /* ✅ Visible por defecto (si JS no corre, nunca queda en blanco) */
        [data-reveal='true'] {
          opacity: 1;
          transform: translateY(0px);
        }

        /* ✅ Solo animamos/ocultamos cuando JS está listo */
        html[data-reveal-ready='true'] [data-reveal='true'] {
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 700ms ease, transform 700ms ease;
        }

        html[data-reveal-ready='true'] [data-reveal='true'][data-revealed='true'] {
          opacity: 1;
          transform: translateY(0px);
        }
      `}</style>
    </div>
  );
}
